import { useCallback, useState } from 'react'
import pLimit from 'p-limit'
import {
  callAuthorizedGraphQLEndpoint,
  updateUserData,
  deleteUser,
} from 'Module/graphQL'
import { authorization } from 'Module/Auth/Auth'
import { BRAND_AMBASSADOR_STATUS_ENUM } from '../../../../shared/constants'

export const getBrandAmbassadors = `
  query (
    $cityId: ID!, 
    $first: Int, 
    $after: String, 
    $hasInventory: Boolean, 
    $hasMissionsBooked: Boolean,
    $statuses: [UserStatusEnum!],
    $search: String,
    $orderBy: UserOrderByEnum,
    $skip: Int,
    $isWithholdPay: Boolean
    $restrictedMissionType: MissionTypeEnum
  ) {
    city(id: $cityId){
      brandAmbassadors(
        first: $first,  
        after: $after, 
        hasInventory: $hasInventory,
        hasMissionsBooked: $hasMissionsBooked,
        statuses: $statuses,
        search: $search,
        orderBy: $orderBy,
        skip: $skip,
        isWithholdPay: $isWithholdPay
        restrictedMissionType: $restrictedMissionType
      ){
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          firstName
          lastName
          isTeamLeader
          status: statusV2
          memberSince
          itemCount
          documentsProvided
          mobilePhone
          email
          recruitmentChannel
          isAttendantOnboarding
          city {
            name
            timezone
          }
          documents {
            latestDocuments {
              id
              url
              comment
              status
              createdAt
              updatedAt
              documentTypeId
              userId
              isPrimary
              rejectReason
              uploadedBy
              actorId
              uploader
              actor
              instructionText
              documentTypeName
              countryCode
            }
          }
          providedPersonalDocuments{
            edges {
              node {
                id
                url
                instructionText
                documentTypeName
                countryCode
              }
            }
          }
          missions {
            totalCount
            nodes {
              totalFlyersDistributed
              status
              dateTime
            }
          }
          withholdPay
          restrictedMissionType
          rating
          tracking {
            interviewTimestamp
            stepInterestedTimestamp
            stepBookedTimestamp
            stepBlockedTimestamp
            stepPreAccessTimestamp
            stepWaitingForReviewTimestamp
            stepApprovedTimestamp
            stepQuizInProgressTimestamp
            stepVideoOnboardingInProgressTimestamp
            stepUninterestedTimestamp
            stepOnSiteTrainingTimestamp
            stepOnsiteMeetingTimestamp
          }
        }
      }
    }
  }
`

export const getCityBrandAmbassadorsMinimal = `
  query (
    $cityId: ID!, 
    $first: Int, 
    $after: String, 
    $hasInventory: Boolean, 
    $hasMissionsBooked: Boolean,
    $statuses: [UserStatusEnum!],
    $search: String,
    $orderBy: UserOrderByEnum,
    $skip: Int,
    $isWithholdPay: Boolean
  ) {
    city(id: $cityId){
      brandAmbassadors(
        first: $first,  
        after: $after, 
        hasInventory: $hasInventory,
        hasMissionsBooked: $hasMissionsBooked,
        statuses: $statuses,
        search: $search,
        orderBy: $orderBy,
        skip: $skip,
        isWithholdPay: $isWithholdPay
      ){
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          firstName
          lastName
          status: statusV2
        }
      }
    }
  }
`

export async function getListCalendarEvents(cityId) {
  const query = `query ($cityId: ID!) {
    listCalendarEvents(cityId: $cityId) {
      userId
      dateTime
      googleEventMetadata
    }
  }`

  return callAuthorizedGraphQLEndpoint(
    query,
    {
      cityId,
    },
    // Calendar events are optional pipeline enrichment. Some cities have no
    // Recruitment Calendar Integration Link configured, which makes this
    // resolver return an error — don't surface a blocking error modal for it.
    { doNotShowErrorModal: true },
  )
}

const cache = {}
export const useAmbassadorsRequest = (cache => cityId => {
  const [
    progressOfGetAllAmbassadors,
    setProgressOfGetAllAmbassadors,
  ] = useState({
    isLoading: false,
  })
  const [isLoadGetAmbassadors, setIsLoadGetAmbassadors] = useState(false)

  const getAmbassadorCalendarEvents = useCallback(async () => {
    try {
      const response = await getListCalendarEvents(cityId)
      const result = await response.json()

      return (result && result.data && result.data.listCalendarEvents) || []
    } catch (error) {
      // Calendar events only annotate booked meetings — a missing/invalid
      // Recruitment Calendar Integration Link (or a base64-encoded error
      // body the gateway returns) must not block the whole pipeline from
      // loading. Degrade gracefully and render without them.
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to load ambassador calendar events; rendering pipeline without them',
        error,
      )
      return []
    }
  }, [cityId])

  const getAmbassadors = useCallback(
    async (args, options = {}) => {
      setIsLoadGetAmbassadors(true)
      const key = JSON.stringify(args)

      if (!options.isFresh && cache[cityId + key]) {
        return cache[cityId + key]
      }

      const response = await callAuthorizedGraphQLEndpoint(
        getBrandAmbassadors,
        {
          cityId,
          ...args,
        },
      )

      const result = (await response.json()).data.city.brandAmbassadors
      setIsLoadGetAmbassadors(false)

      return (cache[cityId + key] = {
        ambassadors: result.nodes,
        totalCount: result.totalCount,
      })
    },
    [cityId, cache],
  )

  const getAllAmbassadors = useCallback(
    async (args, options = {}) => {
      const { progressName, isFresh } = options

      const updProgressInit = name => progress =>
        name
          ? setProgressOfGetAllAmbassadors({ [name]: progress })
          : setProgressOfGetAllAmbassadors(progress)

      const updProgress = updProgressInit(progressName)

      const limit = pLimit(100)
      updProgress({
        isLoading: true,
      })

      /**
       * @param {{ first: number; skip: number; }} params
       * @returns { Promise<{ ambassadors: unknown[]; totalCount: number}> }
       */
      async function request(params) {
        const key = JSON.stringify({ args, params })

        if (!isFresh && cache[cityId + key]) {
          return cache[cityId + key]
        }
        const response = await callAuthorizedGraphQLEndpoint(
          getBrandAmbassadors,
          {
            cityId,
            ...args,
            ...params,
          },
        )
        const result = (await response.json()).data.city.brandAmbassadors

        const ambassadors = await Promise.all(
          result.nodes.map(ba =>
            limit(async () => {
              if (
                (ba.tracking &&
                  !!ba.tracking.stepVideoOnboardingInProgressTimestamp) ||
                ba.status ===
                  BRAND_AMBASSADOR_STATUS_ENUM.VIDEO_ONBOARDING_IN_PROGRESS
              ) {
                // Hardcoded, because we using only 3 videos with quiz flow for all countries, doesnt make sense to fetch videoOnboardingInfo for each ambassador
                const hasQuiz = true
                const videoType = '3V'
                return {
                  ...ba,
                  videoType,
                  hasQuiz,
                }
              } else {
                return { ...ba }
              }
            }),
          ),
        )

        return (cache[cityId + key] = {
          ambassadors,
          totalCount: result.totalCount,
        })
      }

      const pageSize = 200
      const { ambassadors: firstPage, totalCount } = await request({
        first: pageSize,
        skip: 0,
      })

      updProgress({
        isLoading: true,
        label: `200 from ${totalCount}`,
      })

      const pages = Math.max(1, Math.ceil(totalCount / pageSize))
      const otherPages = await Promise.all(
        Array(pages - 1)
          .fill()
          .map((_, n) =>
            limit(async () => {
              const { ambassadors } = await request({
                first: pageSize,
                skip: pageSize * (n + 1),
              })

              updProgress({
                isLoading: true,
                label: `${pageSize * (n + 1)} from ${totalCount}`,
              })

              return ambassadors
            }),
          ),
      )

      const nodes = firstPage.concat(otherPages.flat())

      updProgress({
        isLoading: false,
      })

      return {
        ambassadors: nodes,
        totalCount,
      }
    },
    [cityId, cache, progressOfGetAllAmbassadors],
  )

  const updateAmbassador = useCallback(
    async (id, data) => {
      const authToken = await authorization.getAPIKey()
      const response = await updateUserData(authToken, id, data)
      const result = await response.json()

      return result
    },
    [cityId],
  )

  const deleteAmbassador = useCallback(
    async id => {
      const authToken = await authorization.getAPIKey()
      const response = await deleteUser(authToken, id)
      const result = await response.json()

      return result
    },
    [cityId],
  )

  return {
    getAmbassadors,
    getAllAmbassadors,
    updateAmbassador,
    deleteAmbassador,
    getAmbassadorCalendarEvents,
    isLoadGetAmbassadors,
    progressOfGetAllAmbassadors,
  }
})(cache)
